/**
 * Cache Service for client-side data persistence
 * Uses localStorage to cache agent dashboard data with intelligent expiry
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
  expires_at?: number; // Unix timestamp for expiry
  agent_id?: string;
  generation_timestamp?: string;
  report_version?: string; // Version of the agent report (e.g., "2.8-Corrected")
}

interface AgentCacheMetadata {
  agent_id: string;
  cached_at: number;
  expires_at: number;
  generation_timestamp: string;
  report_version: string; // Version of the agent report
}

const CACHE_VERSION = '1.0.0';
const CACHE_PREFIX = 'indexalpha_cache_';
const AGENT_CACHE_PREFIX = 'agent_';

export class CacheService {
  /**
   * Save data to cache
   */
  static set<T>(key: string, data: T): void {
    try {
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      
      const cacheKey = `${CACHE_PREFIX}${key}`;
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      
      console.log(`✅ Cached data for ${key}`);
    } catch (error) {
      console.error('Failed to cache data:', error);
      // If localStorage is full, try to clear old cache entries
      this.clearOldCache();
    }
  }

  /**
   * Get data from cache
   * @param maxAge - Maximum age in milliseconds (default: 1 hour)
   */
  static get<T>(key: string, maxAge: number = 60 * 60 * 1000): T | null {
    try {
      const cacheKey = `${CACHE_PREFIX}${key}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }
      
      const cacheEntry: CacheEntry<T> = JSON.parse(cached);
      
      // Check version compatibility
      if (cacheEntry.version !== CACHE_VERSION) {
        console.log(`🔄 Cache version mismatch for ${key}, clearing...`);
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      // Check if cache is expired
      const age = Date.now() - cacheEntry.timestamp;
      if (age > maxAge) {
        console.log(`⏰ Cache expired for ${key} (age: ${Math.round(age / 1000)}s)`);
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      console.log(`✅ Using cached data for ${key} (age: ${Math.round(age / 1000)}s)`);
      return cacheEntry.data;
    } catch (error) {
      console.error('Failed to get cached data:', error);
      return null;
    }
  }

  /**
   * Check if cache exists and is valid
   */
  static has(key: string, maxAge: number = 60 * 60 * 1000): boolean {
    return this.get(key, maxAge) !== null;
  }

  /**
   * Clear specific cache entry
   */
  static clear(key: string): void {
    const cacheKey = `${CACHE_PREFIX}${key}`;
    localStorage.removeItem(cacheKey);
    console.log(`🗑️ Cleared cache for ${key}`);
  }

  /**
   * Clear all cache entries
   */
  static clearAll(): void {
    const keys = Object.keys(localStorage);
    let cleared = 0;
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
        cleared++;
      }
    });
    
    console.log(`🗑️ Cleared ${cleared} cache entries`);
  }

  /**
   * Clear old cache entries (older than 24 hours)
   */
  static clearOldCache(): void {
    const keys = Object.keys(localStorage);
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    let cleared = 0;
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cacheEntry = JSON.parse(cached);
            const age = Date.now() - cacheEntry.timestamp;
            if (age > maxAge) {
              localStorage.removeItem(key);
              cleared++;
            }
          }
        } catch (error) {
          // Invalid cache entry, remove it
          localStorage.removeItem(key);
          cleared++;
        }
      }
    });
    
    if (cleared > 0) {
      console.log(`🗑️ Cleared ${cleared} old cache entries`);
    }
  }

  /**
   * Get cache size in bytes
   */
  static getCacheSize(): number {
    let size = 0;
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        const item = localStorage.getItem(key);
        if (item) {
          size += item.length * 2; // UTF-16 characters are 2 bytes each
        }
      }
    });
    
    return size;
  }

  /**
   * Get human-readable cache size
   */
  static getCacheSizeFormatted(): string {
    const bytes = this.getCacheSize();
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  /**
   * Save agent data with custom expiry time
   */
  static setAgent<T>(
    agentId: string,
    data: T,
    expiresAt: number,
    generationTimestamp?: string,
    reportVersion?: string
  ): void {
    try {
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION,
        expires_at: expiresAt,
        agent_id: agentId,
        generation_timestamp: generationTimestamp,
        report_version: reportVersion
      };

      const cacheKey = `${CACHE_PREFIX}${AGENT_CACHE_PREFIX}${agentId}`;
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));

      console.log(
        `✅ Cached ${agentId} data (version: ${reportVersion}, expires: ${new Date(expiresAt).toLocaleString()})`
      );
    } catch (error) {
      console.error(`Failed to cache ${agentId} data:`, error);
      this.clearOldCache();
    }
  }

  /**
   * Get agent data from cache (checks expiry)
   */
  static getAgent<T>(agentId: string): T | null {
    try {
      const cacheKey = `${CACHE_PREFIX}${AGENT_CACHE_PREFIX}${agentId}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const cacheEntry: CacheEntry<T> = JSON.parse(cached);

      // Check version compatibility
      if (cacheEntry.version !== CACHE_VERSION) {
        console.log(`🔄 Cache version mismatch for ${agentId}, clearing...`);
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Check if cache is expired
      if (cacheEntry.expires_at && Date.now() > cacheEntry.expires_at) {
        console.log(`⏰ Cache expired for ${agentId}`);
        localStorage.removeItem(cacheKey);
        return null;
      }

      const age = Date.now() - cacheEntry.timestamp;
      console.log(
        `✅ Using cached ${agentId} data (age: ${Math.round(age / 1000)}s)`
      );
      return cacheEntry.data;
    } catch (error) {
      console.error(`Failed to get cached ${agentId} data:`, error);
      return null;
    }
  }

  /**
   * Check if agent cache is valid
   */
  static isAgentValid(agentId: string): boolean {
    return this.getAgent(agentId) !== null;
  }

  /**
   * Get agent cache metadata
   */
  static getAgentMetadata(agentId: string): AgentCacheMetadata | null {
    try {
      const cacheKey = `${CACHE_PREFIX}${AGENT_CACHE_PREFIX}${agentId}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const cacheEntry: CacheEntry<any> = JSON.parse(cached);

      if (!cacheEntry.expires_at || !cacheEntry.agent_id) {
        return null;
      }

      return {
        agent_id: cacheEntry.agent_id,
        cached_at: cacheEntry.timestamp,
        expires_at: cacheEntry.expires_at,
        generation_timestamp: cacheEntry.generation_timestamp || '',
        report_version: cacheEntry.report_version || 'unknown'
      };
    } catch (error) {
      console.error(`Failed to get ${agentId} metadata:`, error);
      return null;
    }
  }

  /**
   * Get all agent cache keys
   */
  static getAgentCacheKeys(): string[] {
    const keys = Object.keys(localStorage);
    const agentPrefix = `${CACHE_PREFIX}${AGENT_CACHE_PREFIX}`;

    return keys
      .filter(key => key.startsWith(agentPrefix))
      .map(key => key.replace(agentPrefix, ''));
  }

  /**
   * Clear cache for specific agents
   */
  static clearAgents(agentIds: string[]): void {
    agentIds.forEach(agentId => {
      const cacheKey = `${CACHE_PREFIX}${AGENT_CACHE_PREFIX}${agentId}`;
      localStorage.removeItem(cacheKey);
      console.log(`🗑️ Cleared cache for ${agentId}`);
    });
  }

  /**
   * Clear all agent caches
   */
  static clearAllAgents(): void {
    const agentIds = this.getAgentCacheKeys();
    this.clearAgents(agentIds);
    console.log(`🗑️ Cleared all agent caches (${agentIds.length} agents)`);
  }

  /**
   * Get cache statistics
   */
  static getCacheStats(): {
    totalSize: number;
    formattedSize: string;
    agentCount: number;
    agents: {
      id: string;
      size: number;
      expiresIn: number;
      isExpired: boolean;
    }[];
  } {
    const agentIds = this.getAgentCacheKeys();
    const agents = agentIds.map(agentId => {
      const metadata = this.getAgentMetadata(agentId);
      const cacheKey = `${CACHE_PREFIX}${AGENT_CACHE_PREFIX}${agentId}`;
      const cached = localStorage.getItem(cacheKey);
      const size = cached ? cached.length * 2 : 0; // UTF-16 chars = 2 bytes
      const expiresIn = metadata
        ? metadata.expires_at - Date.now()
        : 0;
      const isExpired = expiresIn <= 0;

      return {
        id: agentId,
        size,
        expiresIn: Math.max(0, expiresIn),
        isExpired
      };
    });

    return {
      totalSize: this.getCacheSize(),
      formattedSize: this.getCacheSizeFormatted(),
      agentCount: agentIds.length,
      agents
    };
  }
}

// Clean up old cache entries on initialization
if (typeof window !== 'undefined') {
  CacheService.clearOldCache();
}

export default CacheService;
export type { AgentCacheMetadata };